import type {
  Cart,
  CartLineItem,
  DescriptionLine,
  ICurrentCartService,
  ILeanGQLCartModel,
  IMiniCartLeanLineItemModel,
} from './types';
import { getImageUrl } from '@wix/sdk/media';
import { getFormattingFunction } from '../../utils/getFormattingFunction';
import {
  IWixAPI,
  IPlatformServices,
} from '@wix/native-components-infra/dist/src/types/types';
import { guidToInt } from '../../utils/utils';

export async function getLeanGQLCartFromCart({
  currentCartService,
  wixApi,
  platformServices,
  cart,
}: {
  currentCartService: ICurrentCartService;
  wixApi: IWixAPI;
  platformServices: IPlatformServices;
  cart?: Cart;
}): Promise<ILeanGQLCartModel> {
  cart = cart ?? (await currentCartService.getCurrentCart());
  const formatAmount = getFormattingFunction({
    wixApi,
    platformServices,
    formatingCurrency: cart.customerInfo?.currencyCode,
  });

  return mapCartToLeanGQLCartModel({ cart, formatAmount });
}

function mapCartToLeanGQLCartModel({
  cart,
  formatAmount,
}: {
  cart: Cart;
  formatAmount: (amount: string | number) => string;
}): ILeanGQLCartModel {
  return {
    cartId: cart._id,
    items: (cart.lineItems ?? []).map((lineItem: CartLineItem) =>
      mapLineItemToLeanLineItemModel(lineItem, formatAmount),
    ),
    convertedTotals: {
      itemsTotal: cart.subtotal?.convertedAmount
        ? Number(cart.subtotal.convertedAmount)
        : undefined,
      formattedItemsTotal: formatAmount(cart.subtotal?.convertedAmount ?? 0),
      subTotal: cart.subtotal?.convertedAmount
        ? Number(cart.subtotal.convertedAmount)
        : undefined,
      shipping: 0,
      tax: 0,
      total: Number(cart.subtotal?.convertedAmount),
    },
    convertedCurrencyFormat: { code: cart.customerInfo?.currencyCode },
    buyerNote: cart.note ?? null,
  };
}

function mapLineItemToLeanLineItemModel(
  lineItem: CartLineItem,
  formatAmount: (amount: string | number) => string,
): IMiniCartLeanLineItemModel {
  const product = mapLineItemToProduct(lineItem);
  const comparePrice =
    lineItem.pricing?.breakdown?.fullPrice?.convertedAmount !==
    lineItem.pricing?.unitPrice?.convertedAmount
      ? lineItem.pricing!.breakdown?.fullPrice
      : undefined;
  const priceBeforeDiscount =
    lineItem.pricing?.breakdown?.salePrice?.convertedAmount !==
    lineItem.pricing?.unitPrice?.convertedAmount
      ? lineItem.pricing!.breakdown?.salePrice?.convertedAmount
      : undefined;
  const fileType = (
    lineItem.attributes as { digitalFile?: { fileType: string } }
  )?.digitalFile?.fileType?.toLowerCase();

  return {
    cartItemId: guidToInt(lineItem._id!),
    quantity: lineItem.quantityInfo?.confirmedQuantity,
    inventoryQuantity: lineItem.quantityInfo?.availableQuantity ?? null,
    productId: lineItem.source?.catalogReference?.catalogItemId,
    catalogAppId: lineItem.source?.catalogReference?.appId,
    optionsSelectionsValues: getOptionsSelectionsValues(
      lineItem.attributes?.descriptionLines ?? [],
      product.customTextFields,
    ),
    product,
    sku: lineItem.attributes?.physicalProperties?.sku ?? '',
    convertedPrices: {
      formattedPrice: formatAmount(
        lineItem.pricing?.unitPrice?.convertedAmount ?? 0,
      ),
      formattedComparePrice: comparePrice
        ? formatAmount(comparePrice.convertedAmount!)
        : '',
      formattedPriceBeforeDiscount: priceBeforeDiscount
        ? formatAmount(priceBeforeDiscount)
        : '',
      totalPrice: lineItem.pricing?.totalPrice?.convertedAmount
        ? Number(lineItem.pricing.totalPrice.convertedAmount)
        : 0,
    },
    comparePrice: comparePrice?.amount ? Number(comparePrice.amount) : 0,
    discountRules: (lineItem.discounts ?? []).map((discount) => ({
      name: discount.name!.translated,
    })),
    urlPart: lineItem.attributes?.url?.split('/').pop(),
    digitalProductFileItems: fileType ? [{ fileType }] : [],
    ...product,
    media: product.media[0],
  };
}

function mapLineItemToProduct(lineItem: CartLineItem) {
  const image = lineItem.attributes?.image
    ? getImageUrl(lineItem.attributes.image)
    : undefined;
  const media = image
    ? {
        url: /* istanbul ignore next */ image.id ?? image.filename,
        width: image.width,
        height: image.height,
        mediaType: 'PHOTO',
      }
    : { height: 0, width: 0, mediaType: 'PHOTO', url: '' };
  return {
    id: lineItem.source?.catalogReference?.catalogItemId,
    name: lineItem.name?.translated ?? undefined,
    price: lineItem.pricing?.breakdown?.salePrice?.amount
      ? Number(lineItem.pricing.breakdown.salePrice.amount)
      : 0,
    media: [media],
    weight: lineItem.attributes?.physicalProperties?.weight ?? 0,
    productType:
      lineItem.attributes?.itemType?.preset?.toLowerCase() ??
      lineItem.attributes?.itemType?.custom?.toLowerCase(),
    customTextFields: getCustomTextFields(
      lineItem.source?.catalogReference?.options,
    ),
  };
}

export function getCustomTextFields(options?: Record<string, any> | null) {
  const textFields = options?.customTextFields || {};
  return Object.keys(textFields).map((key) => ({
    title: key,
    value: textFields[key],
  }));
}

export const getOptionsSelectionsValues = (
  descriptionLines: DescriptionLine[],
  customTextFields: { title: string }[],
) => {
  return descriptionLines
    .filter((descriptionLine) => {
      const name =
        descriptionLine.name?.translated ?? descriptionLine.name?.original;
      if (customTextFields.some((field) => field.title === name)) {
        return false;
      }

      const colorOption = getText(descriptionLine.colorInfo);
      const plainTextOption = getText(descriptionLine.plainText);
      return colorOption !== '' || plainTextOption !== '';
    })
    .map((descriptionLine, index) => ({
      id: index,
      title: getText(descriptionLine.name),
      value:
        getText(descriptionLine.colorInfo) ||
        getText(descriptionLine.plainText),
    }));
};

const getText = (text?: { original?: string; translated?: string | null }) =>
  text?.translated ?? text?.original ?? '';
